import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import ApiService from "../../../../../../../../config/api-config";
import { backtoSevenDays } from "../../../../../../../../utils/displayTime";
import { getCiuPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import Spinner from "../../../../../../../../common/Spinner";
import NoDataAvailable from "../../../../../../../../common/NoDataAvailable";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import css from "../../../../common/anomalyMagneticStyle.less";
import {
  getProcessedChartData,
  isRenderRequired,
} from "../../../../common/adUtils";
import ADUsageLineChart from "../../../../genericComponents/ADUsageLineChart";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import useI18n from "../../../../../../../../i18n/useI18n";

const CircuitsUsageChart = (props) => {
  const { tViews } = useI18n("views");
  const [HbrBadge] = reactWrapper(["hbr-badge"]);
  const latestRequest = useRef(0);
  const [timePeriod, setTimePeriod] = useState([]);
  const [ciuLoader, setCiuLoader] = useState(null);
  const [ciuError, setCiuError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [noData, setNoData] = useState(null);
  const [removedList, setRemovedList] = useState([]);
  const [addedCircuit, addReSelectCircuit] = useState([]);
  const [apiCircuitList, setAPICircuitList] = useState([]);

  useEffect(() => {
    // reset state
    setRemovedList([]);
    addReSelectCircuit([]);
    setNoData(null);
    setAPICircuitList([]);
    setCiuError(null);
  }, [props.cuiProps.site]);

  const isRender = useMemo(() => {
    return isRenderRequired(
      props.prevCircuitUsageList, // prev
      props.selectedtrendCircuits.map((item) => item.name), // current
      props.isPrevUsgList, // Update prev
      setNoData, // func for no data
      setRemovedList, // func for removed list
      removedList, // current removed list
      addReSelectCircuit, // func for re-select circuit
      apiCircuitList, // API circuit list
      setAPICircuitList, // func to set API circuit list
    );
  }, [props.selectedtrendCircuits, apiCircuitList]);

  useEffect(() => {
    const runEffect = async () => {
      if (isRender) {
        let selectedCircuits = props.selectedtrendCircuits.map(
          (item) => item.id,
        );
        if (selectedCircuits.length === 0) return;
        const abortController = new AbortController();
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        try {
          let timePeriod = backtoSevenDays();
          setTimePeriod(timePeriod);
          setCiuLoader(true);
          let ciuPayload = getCiuPayload(timePeriod, selectedCircuits);
          let res = await ApiService.getCellularInterfaceUsage(ciuPayload, {
            signal: abortController.signal,
          });
          if (latestRequest.current !== requestId) return;
          if (!res.errorObject) {
            setCiuError(null);
            let processedData = getProcessedChartData(
              res.data.data,
              "circuits",
              timePeriod,
            );
            setChartData(processedData);
            setAPICircuitList(
              props.selectedtrendCircuits.map((item) => item.name),
            );
          } else {
            setCiuError(res.errorObject);
          }
        } catch (error) {
          // Handle error
          if (error.name === "AbortError") return; // Ignore abort error
          // Only process if this is the latest request
          if (latestRequest.current !== requestId) return;
          setCiuError(error);
        } finally {
          setCiuLoader(false);
        }
      }
    };

    runEffect();
  }, [isRender]);

  return (
    <div
      className={css["ad-circuit-usage-charts"]}
      role="region"
      aria-label={tViews("usageBasedAD.usageTabHeader")}
    >
      <h2>
        {tViews("usageBasedAD.usageTabHeader")}
        {tViews("postSpace")}
        <HbrBadge
          slot="suffix"
          pill
          size="small"
          aria-hidden="true"
          data-testid="usage-based-ctusage-site-badge"
        >
          {`${tViews("usageBasedAD.Site")}: ${props.cuiProps.displaySiteName}`}
        </HbrBadge>
      </h2>
      {ciuLoader ? (
        <Spinner />
      ) : ciuError ? (
        <ErrorComponent error={ciuError} role="alert" />
      ) : noData ? (
        <NoDataAvailable />
      ) : (
        <div aria-live="polite">
          {chartData?.data?.length > 0 && (
            <div>
              {timePeriod && (
                <div className="customt-timestamp">
                  <CustomTimeStamp
                    prevTime={timePeriod[0]}
                    currentTime={timePeriod[1]}
                  />
                  <div className="ad-timePeriodTxt">{`${tViews("app360.afHeatmapTimePriodText")}`}</div>
                </div>
              )}
              <ADUsageLineChart
                id="circuit-usage-trend-chart"
                data={chartData.data}
                anomaly_legend={true}
                selectedHour={props.selectedHour}
                removedList={removedList}
                addedCircuit={addedCircuit}
                palette={chartData.palette}
                circuitName={props.cuiProps.displayCircuitName}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CircuitsUsageChart.propTypes = {
  selectedtrendCircuits: PropTypes.array,
  isPrevUsgList: PropTypes.func,
  prevCircuitUsageList: PropTypes.array,
  selectedHour: PropTypes.string,
  cuiProps: PropTypes.object,
};

export default CircuitsUsageChart;
